import React, { useState, useEffect } from 'react';
import { Calendar, CheckCircle, XCircle, Clock, User, Mail, Phone, CalendarDays, TrendingUp, Users, Target } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import StatsCard from './StatsCard';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const BookingManagement = ({ API_BASE }) => {
  const [bookings, setBookings] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchBookings();
  }, [statusFilter]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/api/admin/bookings`, {
        params: { status: statusFilter }
      });
      setBookings(response.data.bookings || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) {
      alert('Please select a status');
      return;
    }

    try {
      await axios.post(`${API_BASE}/api/admin/bookings/${selectedBooking.id}/status`, null, {
        params: { status: newStatus, notes: adminNotes }
      });
      
      alert('Booking status updated successfully!');
      setShowDetailsModal(false);
      setSelectedBooking(null);
      setNewStatus('');
      setAdminNotes('');
      fetchBookings();
    } catch (error) {
      console.error('Error updating booking:', error);
      alert('Failed to update booking status');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      confirmed: 'bg-blue-500/20 text-blue-400',
      completed: 'bg-green-500/20 text-green-400',
      cancelled: 'bg-red-500/20 text-red-400'
    };
    return badges[status] || badges.pending;
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <Clock size={16} />,
      confirmed: <CheckCircle size={16} />,
      completed: <CheckCircle size={16} />,
      cancelled: <XCircle size={16} />
    };
    return icons[status] || icons.pending;
  };

  // Calculate booking statistics
  const calculateBookingStats = () => {
    const totalBookings = bookings.length;
    const pendingBookings = bookings.filter(b => b.status === 'pending').length;
    const confirmedBookings = bookings.filter(b => b.status === 'confirmed').length;
    const completedBookings = bookings.filter(b => b.status === 'completed').length;
    const cancelledBookings = bookings.filter(b => b.status === 'cancelled').length;
    
    // Calculate bookings in last 30 days
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const recentBookings = bookings.filter(b => new Date(b.created_at) >= thirtyDaysAgo).length;
    
    // Calculate conversion rate (confirmed + completed / total)
    const conversionRate = totalBookings > 0 
      ? Math.round(((confirmedBookings + completedBookings) / totalBookings) * 100)
      : 0;
    
    return {
      totalBookings,
      pendingBookings,
      confirmedBookings,
      completedBookings,
      cancelledBookings,
      recentBookings,
      conversionRate
    };
  };

  // Prepare booking status distribution data for chart
  const getStatusDistribution = () => {
    const stats = calculateBookingStats();
    return [
      { name: 'Pending', value: stats.pendingBookings, color: '#F59E0B' },
      { name: 'Confirmed', value: stats.confirmedBookings, color: '#3B82F6' },
      { name: 'Completed', value: stats.completedBookings, color: '#10B981' },
      { name: 'Cancelled', value: stats.cancelledBookings, color: '#EF4444' }
    ];
  };

  // Prepare bookings over time data for trend chart
  const getBookingsTrendData = () => {
    const monthlyData = {};
    
    bookings.forEach(booking => {
      const date = new Date(booking.created_at);
      const monthYear = date.toLocaleDateString('en', { month: 'short', year: '2-digit' });
      monthlyData[monthYear] = (monthlyData[monthYear] || 0) + 1;
    });
    
    // Get last 6 months
    const result = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthYear = date.toLocaleDateString('en', { month: 'short', year: '2-digit' });
      result.push({
        month: monthYear,
        bookings: monthlyData[monthYear] || 0
      });
    }
    
    return result;
  };

  // Prepare service type distribution data for chart
  const getServiceDistribution = () => {
    const serviceCounts = {};
    bookings.forEach(booking => {
      const service = booking.service || 'General Consultation';
      serviceCounts[service] = (serviceCounts[service] || 0) + 1;
    });
    
    const colors = ['#14B8A6', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444'];
    
    return Object.entries(serviceCounts).map(([service, count], index) => ({
      name: service.length > 20 ? service.substring(0, 20) + '...' : service,
      fullName: service,
      value: count,
      color: colors[index % colors.length]
    }));
  };

  const bookingStats = calculateBookingStats();
  const statusData = getStatusDistribution();
  const trendData = getBookingsTrendData();
  const serviceData = getServiceDistribution();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="heading-2 text-white">Booking Management</h2>
        <div className="text-gray-400">Total Bookings: {bookings.length}</div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total Bookings"
          value={bookingStats.totalBookings}
          icon={<CalendarDays size={24} />}
          color="blue"
        />
        <StatsCard
          title="Confirmed"
          value={bookingStats.confirmedBookings}
          icon={<CheckCircle size={24} />}
          color="green"
        />
        <StatsCard
          title="30-Day Bookings"
          value={bookingStats.recentBookings}
          icon={<TrendingUp size={24} />}
          color="purple"
          trend="recent activity"
        />
        <StatsCard
          title="Conversion Rate"
          value={`${bookingStats.conversionRate}%`}
          icon={<Target size={24} />}
          color="turquoise"
        />
      </div>

      {/* Charts Section */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Bookings Trend Chart */}
        <div className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6">
          <h3 className="text-white text-lg font-semibold mb-4">Booking Trend (6 Months)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="month" stroke="#9CA3AF" style={{ fontSize: '12px' }} />
              <YAxis stroke="#9CA3AF" style={{ fontSize: '12px' }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1F2937', 
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#fff'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="bookings" 
                stroke="#14B8A6" 
                strokeWidth={3}
                dot={{ fill: '#14B8A6', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Booking Status Distribution */}
        <div className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6">
          <h3 className="text-white text-lg font-semibold mb-4">Status Distribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1F2937', 
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#fff'
                }}
              />
              <Legend 
                wrapperStyle={{ color: '#9CA3AF' }}
                iconType="circle"
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Service Distribution Chart */}
      <div className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6">
        <h3 className="text-white text-lg font-semibold mb-4">Service Type Distribution</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={serviceData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="name" stroke="#9CA3AF" style={{ fontSize: '12px' }} />
            <YAxis stroke="#9CA3AF" style={{ fontSize: '12px' }} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#1F2937', 
                border: '1px solid #374151',
                borderRadius: '8px',
                color: '#fff'
              }}
              formatter={(value, name, props) => [value, props.payload.fullName]}
            />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {serviceData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="flex gap-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="p-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
        >
          <option value="all">All Bookings</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div className="grid gap-4">
        {loading ? (
          <div className="text-center p-8 text-gray-400">Loading bookings...</div>
        ) : bookings.length === 0 ? (
          <div className="text-center p-8 text-gray-400">No bookings found</div>
        ) : (
          bookings.map((booking) => (
            <div key={booking.id} className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-white font-semibold text-lg">{booking.name}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusBadge(booking.status)}`}>
                      {getStatusIcon(booking.status)}
                      {booking.status}
                    </span>
                  </div>

                  <div className="grid md:grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-gray-300">
                      <Mail size={16} className="text-gray-400" />
                      {booking.email}
                    </div>
                    {booking.phone && (
                      <div className="flex items-center gap-2 text-gray-300">
                        <Phone size={16} className="text-gray-400" />
                        {booking.phone}
                      </div>
                    )}
                    {booking.company && (
                      <div className="flex items-center gap-2 text-gray-300">
                        <User size={16} className="text-gray-400" />
                        {booking.company}
                      </div>
                    )}
                    {booking.preferred_date && (
                      <div className="flex items-center gap-2 text-gray-300">
                        <Calendar size={16} className="text-gray-400" />
                        {new Date(booking.preferred_date).toLocaleDateString()}
                      </div>
                    )}
                  </div>

                  <div className="mt-3">
                    <div className="text-gray-400 text-sm mb-1">Service:</div>
                    <div className="text-white">{booking.service}</div>
                  </div>

                  {booking.message && (
                    <div className="mt-3">
                      <div className="text-gray-400 text-sm mb-1">Message:</div>
                      <p className="text-gray-300 text-sm">{booking.message}</p>
                    </div>
                  )}

                  <div className="mt-3 text-xs text-gray-500">
                    Submitted: {new Date(booking.created_at).toLocaleString()}
                  </div>
                </div>

                <div className="ml-4">
                  <Button
                    onClick={() => {
                      setSelectedBooking(booking);
                      setNewStatus(booking.status);
                      setShowDetailsModal(true);
                    }}
                    className="btn-primary-enhanced text-sm"
                  >
                    Manage
                  </Button>
                </div>
              </div>

              {booking.admin_notes && (
                <div className="mt-4 p-3 bg-gray-800 rounded-lg border-l-4 border-turquoise-500">
                  <div className="text-xs text-gray-400 mb-1">Admin Notes:</div>
                  <div className="text-sm text-gray-300">{booking.admin_notes}</div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {showDetailsModal && selectedBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-gray-700 rounded-16 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="heading-3 text-white mb-4">Manage Booking</h3>
            
            <div className="space-y-4 mb-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-400 mb-1">Client Name</div>
                  <div className="text-white font-medium">{selectedBooking.name}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">Email</div>
                  <div className="text-white">{selectedBooking.email}</div>
                </div>
                {selectedBooking.phone && (
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Phone</div>
                    <div className="text-white">{selectedBooking.phone}</div>
                  </div>
                )}
                {selectedBooking.company && (
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Company</div>
                    <div className="text-white">{selectedBooking.company}</div>
                  </div>
                )}
              </div>

              <div>
                <div className="text-sm text-gray-400 mb-1">Service</div>
                <div className="text-white">{selectedBooking.service}</div>
              </div>

              {selectedBooking.preferred_date && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Preferred Date</div>
                  <div className="text-white">{new Date(selectedBooking.preferred_date).toLocaleDateString()}</div>
                </div>
              )}

              {selectedBooking.message && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Message</div>
                  <div className="text-gray-300 bg-gray-800 rounded-lg p-3">{selectedBooking.message}</div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Update Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Admin Notes</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add notes about this booking..."
                  rows="3"
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  dir="ltr"
                  style={{ direction: 'ltr' }}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleUpdateStatus}
                className="btn-primary-enhanced flex-1"
              >
                Update Booking
              </Button>
              <Button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedBooking(null);
                  setNewStatus('');
                  setAdminNotes('');
                }}
                className="btn-secondary"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingManagement;
